import React, { useState } from 'react';
import {
  Box,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCreateSession } from '../hooks/useCreateSession';
import {
  ICreateSessionInput,
  SESSION_SPORT,
  SESSION_TYPE,
} from '../../../models/sessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomDatePicker from '../../../shared/CustomDatePicker/CustomDatePicker';

interface ManualSessionDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  rosterAthleteIds: string[];
  defaultDate?: string;
  /** Se valorizzato → wizard a singolo atleta pre-selezionato (Addendum v8 EP4) */
  prefillAthleteId?: string;
  prefillAthleteName?: string;
  /** Sessione programmata linkata (compliance pianificato vs actual) */
  prefillLinkedAssignmentId?: string;
}

/** Converte stringa ISO "YYYY-MM-DD" → Date | null */
const isoToDate = (s: string): Date | null => {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

/** Converte Date → stringa "YYYY-MM-DD" */
const dateToIso = (d: Date | null): string => {
  if (!d) return '';
  return d.toISOString().slice(0, 10);
};

/**
 * Form rapido sessione manuale (Addendum v8 Parte 65 — "Dato manuale rapido").
 * Senza file, salva sessione con sRPE auto-calcolato dal backend.
 */
export const ManualSessionDialog: React.FC<ManualSessionDialogProps> = ({
  open,
  onClose,
  teamId,
  rosterAthleteIds,
  defaultDate,
  prefillAthleteId,
  prefillAthleteName,
  prefillLinkedAssignmentId,
}) => {
  const [date, setDate] = useState(
    defaultDate ?? new Date().toISOString().split('T')[0],
  );
  const [duration, setDuration] = useState<number>(75);
  const [rpe, setRpe] = useState<number>(6);
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');

  // Aggiorna data quando defaultDate cambia (per entry point contestuale calendar cell)
  React.useEffect(() => {
    if (open && defaultDate) setDate(defaultDate);
  }, [open, defaultDate]);

  const createMut = useCreateSession();

  if (!open) return null;

  // Athletes target: se prefillAthleteId → singolo atleta, altrimenti team intero
  const athleteIdsTarget = prefillAthleteId ? [prefillAthleteId] : rosterAthleteIds;
  const targetLabel = prefillAthleteId
    ? `atleta singolo: ${prefillAthleteName ?? prefillAthleteId.slice(-6)}`
    : `${rosterAthleteIds.length} atleti (intero team)`;

  const handleSubmit = async () => {
    if (athleteIdsTarget.length === 0) {
      alert('Nessun atleta target');
      return;
    }
    const body: ICreateSessionInput = {
      teamId,
      athleteIds: athleteIdsTarget,
      date,
      duration,
      sport: SESSION_SPORT.FOOTBALL,
      sessionType: SESSION_TYPE.TRAINING,
      name: name || undefined,
      notes: notes || undefined,
      internalLoad: { rpe },
      linkedAssignmentId: prefillLinkedAssignmentId,
    };
    try {
      await createMut.mutateAsync(body);
      onClose();
    } catch (e: any) {
      alert(`Errore: ${e?.message ?? 'sconosciuto'}`);
    }
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={1000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="lg"
        p={6}
        maxWidth="500px"
        width="90%"
        onClick={(e) => e.stopPropagation()}
      >
        <Text fontSize="xl" fontWeight="700" mb={1}>
          Nuova sessione manuale
        </Text>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Target: {targetLabel}
          {prefillLinkedAssignmentId && ' · linked al planned'}
        </Text>
        <VStack gap={3} alignItems="stretch">
          <Box>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Nome (opzionale)
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Es. Allenamento tattico PM"
            />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Data
            </Text>
            <CustomDatePicker
              date={isoToDate(date)}
              onChange={(d) => setDate(dateToIso(d))}
              placeholder="gg/mm/aaaa"
              showIcon
              dateFormat="dd/MM/yyyy"
            />
          </Box>
          <HStack gap={3}>
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                Durata (min)
              </Text>
              <Input
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value, 10) || 0)}
              />
            </Box>
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                RPE squadra (0-10)
              </Text>
              <Input
                type="number"
                step="0.5"
                min={0}
                max={10}
                value={rpe}
                onChange={(e) =>
                  setRpe(parseFloat(e.target.value) || 0)
                }
              />
            </Box>
          </HStack>
          <Box>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Note (opzionale)
            </Text>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note libere"
            />
          </Box>
          <Text fontSize="xs" color="gray.500">
            sRPE = RPE × durata calcolato automaticamente. Atleti coinvolti:{' '}
            <strong>{athleteIdsTarget.length}</strong>{' '}
            {prefillAthleteId ? '(singolo)' : '(intero team)'}.
          </Text>
          <HStack justifyContent="flex-end" gap={2} mt={2}>
            <CustomButton color="text" onClick={onClose}>
              Annulla
            </CustomButton>
            <CustomButton
              color="primary"
              onClick={handleSubmit}
              isLoading={createMut.isPending}
            >
              Salva sessione
            </CustomButton>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};
